import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Download, Save, CheckCircle2, Wand2, Home,
} from "lucide-react";
import { projectsApi } from "@/lib/api";
import {
  whMetrics, whLineTotal, whGrandTotal, buildStandardWHLines,
  fmt, fmtMoney, WOOD_SYSTEM_TYPES, DEFAULT_WH,
} from "@/lib/calc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const newWall = () => ({ id: crypto.randomUUID(), name: "", width: "", height: "" });
const newQuoteLine = () => ({ id: crypto.randomUUID(), description: "", quantity: 0, unit: "m²", unit_price: 0 });

export default function WoodenHouseEditor({ id, initialProject }) {
  const nav = useNavigate();
  const [project, setProject] = useState(() => ({
    ...initialProject,
    system_type: initialProject.system_type || "clt",
    wooden_house: { ...DEFAULT_WH, ...(initialProject.wooden_house || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [confirmGen, setConfirmGen] = useState(false);
  const [deleteLine, setDeleteLine] = useState(null);
  const saveTimer = useRef(null);

  const wh = project.wooden_house;
  const metrics = whMetrics(wh);
  const total = whGrandTotal(wh.quote_lines);

  const persist = useCallback(async (p) => {
    setSaving(true);
    try {
      await projectsApi.update(id, {
        name: p.name, category: "wooden_house", system_type: p.system_type,
        prepared_by: p.prepared_by, project_date: p.project_date,
        price_mode: p.price_mode || "live", line_items: [],
        wooden_house: p.wooden_house,
        variant_group: p.variant_group || null, group_name: p.group_name || null,
      });
      setSavedAt(Date.now());
    } catch (e) {
      toast.error("Otomatik kayıt başarısız");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = (mutator) => {
    setProject((prev) => {
      const next = mutator({ ...prev });
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };

  const setMeta = (field, value) => update((p) => ({ ...p, [field]: value }));
  const changeSystem = async (v) => {
    clearTimeout(saveTimer.current);
    const next = { ...project, system_type: v };
    setProject(next);
    await persist(next);
    window.location.reload();
  };
  const setWH = (field, value) => update((p) => ({ ...p, wooden_house: { ...p.wooden_house, [field]: value } }));

  const setWall = (side, idx, field, value) => update((p) => {
    const arr = p.wooden_house[side].slice();
    arr[idx] = { ...arr[idx], [field]: value };
    return { ...p, wooden_house: { ...p.wooden_house, [side]: arr } };
  });
  const addWall = (side) => update((p) => ({ ...p, wooden_house: { ...p.wooden_house, [side]: [...p.wooden_house[side], newWall()] } }));
  const removeWall = (side, idx) => update((p) => {
    const arr = p.wooden_house[side].slice();
    arr.splice(idx, 1);
    return { ...p, wooden_house: { ...p.wooden_house, [side]: arr } };
  });

  const setLine = (idx, field, value) => update((p) => {
    const arr = p.wooden_house.quote_lines.slice();
    arr[idx] = { ...arr[idx], [field]: value };
    return { ...p, wooden_house: { ...p.wooden_house, quote_lines: arr } };
  });
  const addLine = () => update((p) => ({ ...p, wooden_house: { ...p.wooden_house, quote_lines: [...p.wooden_house.quote_lines, newQuoteLine()] } }));
  const removeLine = (idx) => update((p) => {
    const arr = p.wooden_house.quote_lines.slice();
    arr.splice(idx, 1);
    return { ...p, wooden_house: { ...p.wooden_house, quote_lines: arr } };
  });

  const generateLines = () => {
    update((p) => {
      const m = whMetrics(p.wooden_house);
      return { ...p, wooden_house: { ...p.wooden_house, quote_lines: buildStandardWHLines(p.wooden_house, m) } };
    });
    setConfirmGen(false);
    toast.success("Standart teklif satırları oluşturuldu");
  };

  const exportExcel = () => window.open(projectsApi.exportUrl(id), "_blank");

  const numInput = (val, onChange, testid, extra = "") => (
    <input type="number" step="0.01" value={val ?? ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
      className={`w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 tabular-nums text-right outline-none focus:ring-2 focus:ring-amber-600 ${extra}`} />
  );

  const metrajFields = [
    ["floor_m2", "Zemin (m²)"],
    ["roof_m2", "Çatı (m²)"],
    ["veranda_m2", "Veranda (m²)"],
    ["balcony_m2", "Balkon (m²)"],
  ];

  const summary = [
    ["İç duvar alanı", metrics.interiorWallArea, "m²"],
    ["Dış duvar alanı", metrics.exteriorWallArea, "m²"],
    ["İç cephe kaplama", metrics.interiorCladding, "m²", true],
    ["Dış cephe kaplama", metrics.exteriorCladding, "m²", true],
  ];

  return (
    <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => nav("/")} data-testid="back-btn"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Tüm projeler
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="save-status">
            {saving ? (<><Save className="w-3.5 h-3.5 animate-pulse" /> Kaydediliyor…</>)
              : savedAt ? (<><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Kaydedildi</>) : null}
          </span>
          <button onClick={exportExcel} data-testid="export-excel-btn"
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
            <Download className="w-4 h-4" /> Excel'e Aktar
          </button>
        </div>
      </div>

      {/* Header block */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Proje adı</label>
            <input data-testid="header-name-input" value={project.name}
              onChange={(e) => setMeta("name", e.target.value)}
              className="mt-1 w-full font-heading text-2xl font-extrabold text-slate-900 bg-transparent border-b border-transparent focus:border-amber-500 outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ahşap ev sistemi</label>
            <Select value={project.system_type} onValueChange={changeSystem}>
              <SelectTrigger data-testid="system-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(WOOD_SYSTEM_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hazırlayan</label>
            <input data-testid="header-preparedby-input" value={project.prepared_by || ""}
              onChange={(e) => setMeta("prepared_by", e.target.value)} placeholder="İsim"
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1" />
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Metraj inputs */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 space-y-4">
          <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
            <Home className="w-5 h-5 text-amber-600" /> Metraj Girdileri
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {metrajFields.map(([key, label]) => (
              <div key={key}>
                <label className="text-xs font-medium text-slate-500">{label}</label>
                {numInput(wh[key], (v) => setWH(key, v), `wh-${key}`)}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div>
              <label className="text-xs font-medium text-slate-500">İç kaplama katsayısı</label>
              {numInput(wh.interior_coeff, (v) => setWH("interior_coeff", v), "wh-interior_coeff")}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Dış kaplama katsayısı</label>
              {numInput(wh.exterior_coeff, (v) => setWH("exterior_coeff", v), "wh-exterior_coeff")}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">İç kapı/doğrama boşluğu (m²)</label>
              {numInput(wh.interior_openings_m2, (v) => setWH("interior_openings_m2", v), "wh-interior_openings")}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Dış kapı/doğrama boşluğu (m²)</label>
              {numInput(wh.exterior_openings_m2, (v) => setWH("exterior_openings_m2", v), "wh-exterior_openings")}
            </div>
          </div>
        </div>

        {/* Computed summary */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Otomatik Hesaplananlar</h2>
          <div className="grid grid-cols-2 gap-3">
            {summary.map(([label, value, unit, hl]) => (
              <div key={label} className={`rounded-md p-4 ${hl ? "bg-amber-50 border border-amber-200" : "bg-slate-50 border border-slate-200"}`}>
                <div className="text-xs text-slate-500">{label}</div>
                <div className={`text-xl font-extrabold tabular-nums ${hl ? "text-amber-900" : "text-slate-900"}`}
                  data-testid={`summary-${label}`}>
                  {fmt(value)} <span className="text-sm font-medium text-slate-400">{unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Wall editors */}
      <div className="grid gap-5 lg:grid-cols-2">
        {[["interior_walls", "İç Duvarlar"], ["exterior_walls", "Dış Duvarlar"]].map(([side, title]) => (
          <div key={side} className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
              <button onClick={() => addWall(side)} data-testid={`add-wall-${side}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Plus className="w-4 h-4" /> Duvar ekle
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="text-left pb-2">Ad</th>
                  <th className="text-right pb-2 w-24">En (m)</th>
                  <th className="text-right pb-2 w-24">Boy (m)</th>
                  <th className="text-right pb-2 w-24">Alan m²</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {wh[side].length === 0 ? (
                  <tr><td colSpan={5} className="py-4 text-center text-slate-400 text-sm">Duvar eklenmedi.</td></tr>
                ) : wh[side].map((w, idx) => (
                  <tr key={w.id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2">
                      <input value={w.name} placeholder={`Duvar ${idx + 1}`} data-testid={`${side}-${idx}-name`}
                        onChange={(e) => setWall(side, idx, "name", e.target.value)}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber-600" />
                    </td>
                    <td className="py-1.5 px-1">
                      <input type="number" step="0.01" value={w.width} data-testid={`${side}-${idx}-width`}
                        onChange={(e) => setWall(side, idx, "width", e.target.value)}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                    </td>
                    <td className="py-1.5 px-1">
                      <input type="number" step="0.01" value={w.height} data-testid={`${side}-${idx}-height`}
                        onChange={(e) => setWall(side, idx, "height", e.target.value)}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                    </td>
                    <td className="py-1.5 px-1 text-right tabular-nums font-medium text-slate-700">
                      {fmt((parseFloat(w.width) || 0) * (parseFloat(w.height) || 0))}
                    </td>
                    <td className="py-1.5 text-center">
                      <button onClick={() => removeWall(side, idx)} data-testid={`${side}-${idx}-delete`}
                        className="p-1 rounded text-red-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Quote lines */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-slate-100">
          <h2 className="font-heading text-lg font-bold text-slate-900">Teklif Satırları</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setConfirmGen(true)} data-testid="generate-lines-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100">
              <Wand2 className="w-4 h-4" /> Standart satırları oluştur
            </button>
            <button onClick={addLine} data-testid="add-line-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Plus className="w-4 h-4" /> Satır ekle
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-slate-100">
                {["Açıklama", "Miktar", "Birim", "Birim Fiyat", "Tutar", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i >= 1 && i <= 4 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wh.quote_lines.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="quote-lines-empty">
                  Henüz satır yok. Metrajı girip "Standart satırları oluştur" ile başlayın.
                </td></tr>
              ) : wh.quote_lines.map((line, idx) => (
                <tr key={line.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`quote-line-${idx}`}>
                  <td className="p-0">
                    <input value={line.description} placeholder="Açıklama" data-testid={`quote-${idx}-desc`}
                      onChange={(e) => setLine(idx, "description", e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-transparent outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="p-0 w-28">
                    <input type="number" step="0.01" value={line.quantity} data-testid={`quote-${idx}-qty`}
                      onChange={(e) => setLine(idx, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-full px-3 py-2 text-sm text-right tabular-nums bg-transparent outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="p-0 w-20">
                    <input value={line.unit} data-testid={`quote-${idx}-unit`}
                      onChange={(e) => setLine(idx, "unit", e.target.value)}
                      className="w-full px-3 py-2 text-sm text-right bg-transparent outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="p-0 w-32">
                    <input type="number" step="0.01" value={line.unit_price} data-testid={`quote-${idx}-price`}
                      onChange={(e) => setLine(idx, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-full px-3 py-2 text-sm text-right tabular-nums bg-transparent outline-none focus:ring-2 focus:ring-amber-600" />
                  </td>
                  <td className="px-3 py-2 text-sm text-right tabular-nums font-bold text-slate-900" data-testid={`quote-${idx}-total`}>
                    {fmtMoney(whLineTotal(line))}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => setDeleteLine(idx)} data-testid={`quote-${idx}-delete`}
                      className="p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-amber-50 border-t-2 border-amber-200">
                <td colSpan={4} className="px-3 py-3 text-right text-sm font-bold uppercase tracking-wider text-slate-700">Genel Toplam</td>
                <td className="px-3 py-3 text-right text-lg font-extrabold tabular-nums text-amber-900 whitespace-nowrap" data-testid="grand-total">{fmtMoney(total)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <AlertDialog open={confirmGen} onOpenChange={setConfirmGen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Standart satırları oluştur?</AlertDialogTitle>
            <AlertDialogDescription>
              Mevcut teklif satırları, metraja göre hesaplanan standart satırlarla değiştirilecek. Devam edilsin mi?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={generateLines} data-testid="confirm-generate" className="bg-amber-600 hover:bg-amber-700">Oluştur</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteLine !== null} onOpenChange={() => setDeleteLine(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Satırı sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu teklif satırı kaldırılacak.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => { removeLine(deleteLine); setDeleteLine(null); }} data-testid="confirm-delete-line" className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
